import React, { useState } from 'react';
import {
  IAuditoriaProvisaoIssResultado,
  IItemProvisaoIss,
  IConciliadoProvisaoIss,
} from '../interfaces/IAuditoriaProvisaoIss';
import { formatCurrency, formatDate } from '../utils/formatters';

interface Props {
  resultado: IAuditoriaProvisaoIssResultado;
}


const getIconeStatus = (status: string) => {
  switch (status) {
    case 'ERRO':
      return 'bi-exclamation-circle-fill';
    case 'ALERTA':
      return 'bi-exclamation-triangle-fill';
    case 'AVISO':
      return 'bi-info-circle-fill';
    default:
      return 'bi-check-circle-fill';
  }
};

// COR-293: drill-down a nível de lançamento — todo ERRO precisa mostrar não só o faturamento
// agregado do mês, mas os lançamentos reais de Receita de Serviços que o compõem.
const LIMITE_LANCAMENTOS_VISIVEIS = 10;

const BlocoLancamentosRelacionados: React.FC<{ item: IItemProvisaoIss }> = ({ item }) => {
  const [mostrarTodos, setMostrarTodos] = useState(false);
  const lancamentos = item.lancamentosRelacionados || [];

  if (lancamentos.length === 0) return null;

  const visiveis = mostrarTodos ? lancamentos : lancamentos.slice(0, LIMITE_LANCAMENTOS_VISIVEIS);

  return (
    <div className='mt-2'>
      <strong>Lançamentos Relacionados ({lancamentos.length}):</strong>
      <div className='table-responsive mt-1'>
        <table className='auditoria-table' style={{ fontSize: 12 }}>
          <thead>
            <tr>
              <th>Data</th>
              <th>Nº Lançamento</th>
              <th>Conta</th>
              <th>Histórico</th>
              <th className='text-end'>Valor</th>
            </tr>
          </thead>
          <tbody>
            {visiveis.map((lanc, idx) => (
              <tr key={`${lanc.numeroLancamento}-${lanc.data}-${idx}`}>
                <td>{formatDate(lanc.data)}</td>
                <td><code>{lanc.numeroLancamento || '-'}</code></td>
                <td><code>{lanc.contaContabil}</code></td>
                <td>{lanc.historico}</td>
                <td className='text-end'>{formatCurrency(lanc.valor)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {lancamentos.length > LIMITE_LANCAMENTOS_VISIVEIS && !mostrarTodos && (
        <button
          type='button'
          className='btn btn-link btn-sm p-0 mt-1'
          style={{ fontSize: 12 }}
          onClick={() => setMostrarTodos(true)}
        >
          + {lancamentos.length - LIMITE_LANCAMENTOS_VISIVEIS} lançamento(s) adicional(is)
        </button>
      )}
    </div>
  );
};

const AuditoriaProvisaoIssDetalhe: React.FC<Props> = ({ resultado }) => {
  const [itemExpandido, setItemExpandido] = useState<string | null>(null);
  const [fundamentacaoAberta, setFundamentacaoAberta] = useState(false);

  const provisoesAusentes = resultado.provisoesAusentes || [];
  const mesesConciliados = resultado.mesesConciliados || [];

  const alternarExpandir = (chave: string) => {
    setItemExpandido(itemExpandido === chave ? null : chave);
  };

  return (
    <>
      <div className={`auditoria-alert status-${resultado.status}`}>
        <i className={`bi ${getIconeStatus(resultado.status)} auditoria-alert-icon`} />
        <div>
          <strong>Auditoria de Provisão Mensal de ISS — {resultado.status}</strong>
          <div>{resultado.mensagemDirecionamento}</div>
        </div>
      </div>

      <div className='auditoria-kpis'>
        <div className={`auditoria-kpi ${resultado.impactoFiscalTotal > 0 ? 'auditoria-kpi-critico' : 'auditoria-kpi-disabled'}`}>
          <div className='auditoria-kpi-label'>Risco Fiscal Estimado</div>
          <div className='auditoria-kpi-value'>{formatCurrency(resultado.impactoFiscalTotal)}</div>
          {resultado.impactoFiscalTotal === 0 ? (
            <span className='auditoria-kpi-selo'>Zero Desperdício</span>
          ) : (
            <div className='auditoria-kpi-help'>Multa ECD potencial (5% limitada a 1% da receita bruta do período) para competências com indício de ausência de provisão de ISS — sujeita a validação, não é penalidade confirmada.</div>
          )}
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Erros / Alertas / Avisos</div>
          <div className='auditoria-kpi-value'>
            <span className='auditoria-contador auditoria-contador-erro'>{resultado.totalErros}</span>{' '}
            <span className='auditoria-contador auditoria-contador-alerta'>{resultado.totalAlertas}</span>{' '}
            <span className='auditoria-contador auditoria-contador-aviso'>{resultado.totalAvisos}</span>
          </div>
          <div className='auditoria-kpi-help'>Competências com faturamento de serviços e ausência de redutora de ISS/ISS a Recolher no período.</div>
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Fundamentação</div>
          <div className='auditoria-kpi-value'>
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={() => setFundamentacaoAberta(!fundamentacaoAberta)}
            >
              <i className='bi bi-journal-check me-1' />
              {fundamentacaoAberta ? 'Ocultar fundamentação' : 'Ver fundamentação legal'}
            </button>
          </div>
          <div className='auditoria-kpi-help'>Base fiscal e contábil aplicada a esta auditoria.</div>
        </div>
      </div>

      {fundamentacaoAberta && (
        <div className='auditoria-card'>
          <div><strong>Fundamentação Fiscal:</strong> {resultado.fundamentacaoFiscal}</div>
          <div className='mt-2'><strong>Fundamentação Contábil:</strong> {resultado.fundamentacaoContabil}</div>
        </div>
      )}

      <div className='auditoria-card'>
        <h3>Indícios de Provisão de ISS Pendente de Validação</h3>
        <p className='text-muted'>
          Competências com faturamento de serviços apurado e nenhum lançamento localizado na conta redutora de ISS
          nem em ISS a Recolher no mesmo mês. A ausência isolada não comprova irregularidade fiscal confirmada —
          requer validação adicional da competência.
        </p>

        {provisoesAusentes.length === 0 && (
          <div className='text-muted mt-4'>Nenhum indício de provisão ausente identificado no período.</div>
        )}

        {provisoesAusentes.length > 0 && (
          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Competência</th>
                  <th className='text-end'>Faturamento de Serviços</th>
                  <th className='text-end'>Multa ECD Potencial</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {provisoesAusentes.map((item: IItemProvisaoIss, idx) => {
                  const chave = `iss-${item.empresaId}-${item.competencia}-${idx}`;
                  const expandido = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo status-alerta' onClick={() => alternarExpandir(chave)}>
                        <td><strong>{item.competencia}</strong></td>
                        <td className='text-end'>{formatCurrency(item.faturamentoServicos)}</td>
                        <td className='text-end'>{formatCurrency(item.impostoDevidoEstimado)}</td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={4} style={{ background: '#fef2f2' }}>
                            <div className='py-2 px-3'>
                              <div><strong>Alerta:</strong> faturamento de serviços sem lançamento localizado na redutora de ISS nem em ISS a Recolher — indício que requer validação adicional, não erro fiscal confirmado.</div>
                              <div><strong>Competência:</strong> {item.competencia}.</div>
                              <div><strong>Faturamento de Serviços:</strong> {formatCurrency(item.faturamentoServicos)}.</div>
                              <div><strong>Multa ECD Potencial (5% limitada a 1% da receita bruta — não é penalidade confirmada):</strong> {formatCurrency(item.impostoDevidoEstimado)}.</div>
                              <div><strong>Conta(s) de Receita:</strong> {item.contasReceita || 'não identificada(s)'}.</div>
                              <div><strong>Fundamentação Fiscal:</strong> {resultado.fundamentacaoFiscal}</div>
                              <div><strong>Fundamentação Contábil:</strong> {resultado.fundamentacaoContabil}</div>
                              <div className='mt-1 text-muted' style={{ fontSize: 12 }}>{item.memoriaCalculo}</div>
                              <BlocoLancamentosRelacionados item={item} />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {mesesConciliados.length > 0 && (
        <div className='auditoria-card'>
          <h3>
            <i className='bi bi-check-circle-fill text-success me-2' style={{ fontSize: 18 }} />
            Competências Verificadas e Conciliadas
          </h3>
          <p className='text-muted'>
            Meses com faturamento de serviços que tiveram lançamento na conta redutora de ISS e/ou em ISS a
            Recolher no mesmo período — veja o diagnóstico de cada competência para saber qual grupo determinou a
            conciliação.
          </p>

          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Competência</th>
                  <th className='text-end'>Faturamento de Serviços</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {mesesConciliados.map((item: IConciliadoProvisaoIss, idx) => {
                  const chave = `issc-${item.empresaId}-${item.competencia}-${idx}`;
                  const expandido = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo' onClick={() => alternarExpandir(chave)}>
                        <td><strong>{item.competencia}</strong></td>
                        <td className='text-end'>{formatCurrency(item.faturamentoServicos)}</td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={3} style={{ background: '#f0fdf4' }}>
                            <div className='py-2 px-3'>
                              <i className='bi bi-check-circle-fill text-success me-1' />
                              <strong>Diagnóstico:</strong> {item.mensagemDiagnostico}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
};

export default AuditoriaProvisaoIssDetalhe;
